import {
  broadcastAppSessionLogout,
  logoutAppSession,
  setAppSessionId,
} from 'eds-app-session';
import { getAppCodeForPath } from '@/utils/get-app-code-for-path';
import { APP_SESSION_ENDPOINTS } from '@/services/app-session/app-session-config';

export const APP_SESSION_END_TIMEOUT_MS = 2000;

export interface EndAppSessionOptions {
  /** Distinguishes an explicit sign out from an idle timeout for other tabs. */
  reason?: 'user' | 'timeout';
  /** False when reacting to another tab's logout, so the message does not echo. */
  broadcast?: boolean;
  /** Overridable for tests; defaults to the live location. */
  pathname?: string;
}

/**
 * Ends the app session for whichever native app this tab is in. On a platform
 * page there is nothing to end, but the broadcast still goes out because sign
 * out is a platform-level action and other tabs may be inside an app.
 *
 * The broadcast goes first so every tab has its end call in flight before the
 * platform sign out invalidates the cookie. Never rejects: session tracking
 * must not block the user from signing out.
 */
export async function endAppSession({
  reason = 'user',
  broadcast = true,
  pathname,
}: EndAppSessionOptions = {}): Promise<void> {
  if (broadcast) {
    broadcastAppSessionLogout(reason);
  }

  const appCode = getAppCodeForPath(pathname ?? window.location.pathname);

  setAppSessionId(null);

  if (!appCode) {
    return;
  }

  const deadline = new Promise<void>((resolve) => {
    setTimeout(resolve, APP_SESSION_END_TIMEOUT_MS);
  });

  const ended = logoutAppSession(APP_SESSION_ENDPOINTS.logout, appCode).catch(
    (error: unknown) => {
      console.error('Failed to end app session:', error);
    }
  );

  await Promise.race([ended, deadline]);
}
